import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  parsePixEmv,
  PixEmvDecoded,
  validatePixEmv,
} from '@gatewee/utils/validators/pix-emv.validator';
import {FeeSnapshot} from '@gatewee/types/fee';
import {WithdrawalAmountInputMode} from '@gatewee/utils';
import {TranslateService} from '@ngx-translate/core';

export type PixShellKind = 'dialog' | 'pageDrawer';

@Component({
  selector: 'app-withdrawal-emv-form',
  templateUrl: './withdrawal-emv-form.component.html',
  standalone: false,
})
export class WithdrawalEmvFormComponent {
  @Input() pixShellKind: PixShellKind = 'dialog';
  @Input() feeSnapshot: FeeSnapshot | null = null;

  @ViewChild('emvActionFooter', {read: TemplateRef})
  private emvActionFooterTplRef?: TemplateRef<unknown>;

  readonly form = new FormGroup({
    emv: new FormControl('', {
      validators: [Validators.required, validatePixEmv],
    }),
  });

  submitted = false;

  /** Last successfully parsed EMV payload (used for handoff to the transfer value step). */
  decodedPreview: PixEmvDecoded | null = null;

  amountInputMode: WithdrawalAmountInputMode = 'addFees';

  @Output() readonly backRequested = new EventEmitter<void>();

  @Output() readonly continueRequested = new EventEmitter<void>();

  constructor(private readonly translate: TranslateService) {}

  /** Used by `PixAreaComponent` for the page-drawer footer slot. */
  get actionFooterTpl(): TemplateRef<unknown> | null {
    return this.emvActionFooterTplRef ?? null;
  }

  /** Drawer / embedded header title for the EMV code step. */
  get drawerTitleKey(): string {
    return 'features.withdrawals.modalPix.emvDrawerTitle';
  }

  get emvCustomMessages(): Record<string, string> {
    const p = 'features.withdrawals.modalPix';
    return {
      required: this.translate.instant(`${p}.emvRequired`),
      invalidPixEmv: this.translate.instant(`${p}.emvInvalid`),
    };
  }

  get canContinueCode(): boolean {
    const ctrl = this.form.get('emv');
    return !!ctrl && ctrl.valid;
  }

  get canProceedFooter(): boolean {
    return this.canContinueCode;
  }

  /** Parent returns to hub when `false`. */
  handleBack(): boolean {
    return false;
  }

  reset(): void {
    this.decodedPreview = null;
    this.amountInputMode = 'addFees';
    this.submitted = false;
    this.form.reset({emv: ''});
  }

  /** Pre-fills the EMV input (e.g. from a QR scan) and triggers validation. */
  setEmvValue(value: string): void {
    const ctrl = this.form.get('emv');
    if (!ctrl) {
      return;
    }
    ctrl.setValue(value.trim());
    ctrl.markAsTouched();
    ctrl.updateValueAndValidity();
    this.submitted = false;
    this.decodedPreview = null;
  }

  /** Validates EMV code and hands off to the transfer value step (via parent). */
  attemptContinue(): void {
    this.submitted = true;
    this.form.markAllAsTouched();
    if (!this.canContinueCode) {
      return;
    }
    const decoded = parsePixEmv(String(this.form.get('emv')?.value ?? ''));
    if (!decoded) {
      return;
    }
    this.decodedPreview = decoded;
    this.continueRequested.emit();
  }
}
